import React from 'react';
import { TrendingUp, TrendingDown, Minus, HelpCircle } from 'lucide-react';
import { Tooltip } from '@mui/material';

interface KPICardProps {
    title: string;
    value: string | number;
    change?: number;
    changeLabel?: string;
    icon: React.ReactNode;
    tooltip?: string;
    color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
    format?: 'currency' | 'percentage' | 'number' | 'text';
    subtitle?: React.ReactNode;
    onClick?: () => void;
}

const iconBgClasses = {
    primary: 'bg-primary-10',
    success: 'bg-success-10',
    warning: 'bg-warning-10',
    error: 'bg-error-10',
    info: 'bg-info-10',
};

export const KPICard: React.FC<KPICardProps> = ({
    title,
    value,
    change,
    changeLabel,
    icon,
    tooltip,
    color = 'primary',
    format = 'text',
    subtitle,
    onClick,
}) => {
    const formattedValue = React.useMemo(() => {
        if (typeof value === 'string') return value;
        switch (format) {
            case 'currency':
                return new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: 'EUR',
                }).format(value);
            case 'percentage':
                return `${value.toFixed(1)}%`;
            case 'number':
                return new Intl.NumberFormat('fr-FR').format(value);
            default:
                return String(value);
        }
    }, [value, format]);

    const renderChangeIndicator = () => {
        if (change === undefined) return null;

        const isPositive = change > 0;
        const isNegative = change < 0;
        const isNeutral = change === 0;

        return (
            <div className={`flex items-center gap-1 text-xs font-medium ${isPositive ? 'text-success' : isNegative ? 'text-error' : 'text-foreground-secondary'
                }`}>
                {isPositive && <TrendingUp className="h-3 w-3" />}
                {isNegative && <TrendingDown className="h-3 w-3" />}
                {isNeutral && <Minus className="h-3 w-3" />}
                <span>{isPositive ? '+' : ''}{change.toFixed(1)}%</span>
                {changeLabel && <span className="text-foreground-secondary">vs {changeLabel}</span>}
            </div>
        );
    };

    return (
        <div
            className={`bg-surface border border-border rounded-xl p-3 transition-all duration-200 ${onClick ? 'cursor-pointer hover:shadow-md hover:border-primary-30' : ''}`}
            onClick={onClick}
        >
            <div className="flex items-start justify-between mb-2">
                <div className={`p-1.5 rounded-lg ${iconBgClasses[color]}`}>
                    {icon}
                </div>
                {tooltip && (
                    <Tooltip title={tooltip} arrow placement="top">
                        <div className="cursor-help">
                            <HelpCircle className="h-4 w-4 text-foreground-secondary hover:text-foreground transition-colors" />
                        </div>
                    </Tooltip>
                )}
            </div>

            <div className="space-y-0.5">
                <h3 className="text-xs font-medium text-foreground-secondary">{title}</h3>
                <p className="text-xl font-bold text-foreground">{formattedValue}</p>
                {renderChangeIndicator()}
                {subtitle && <div className="pt-1">{subtitle}</div>}
            </div>
        </div>
    );
};

interface KPICardGridProps {
    children: React.ReactNode;
    columns?: 2 | 3 | 4 | 5 | 6 | 7;
}

export const KPICardGrid: React.FC<KPICardGridProps> = ({ children, columns = 4 }) => {
    const gridCols = {
        2: 'grid-cols-2',
        3: 'grid-cols-2 lg:grid-cols-3',
        4: 'grid-cols-2 lg:grid-cols-4',
        5: 'grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
        6: 'grid-cols-2 lg:grid-cols-3 xl:grid-cols-6',
        7: 'grid-cols-2 lg:grid-cols-3 xl:grid-cols-7',
    };

    return (
        <div className={`grid ${gridCols[columns]} gap-4`}>
            {children}
        </div>
    );
};

interface BadgeKPIProps {
    label: string;
    value: number;
    total: number;
    variant?: 'success' | 'warning' | 'error';
}

export const BadgeKPI: React.FC<BadgeKPIProps> = ({ label, value, total, variant = 'success' }) => {
    const percentage = total > 0 ? (value / total) * 100 : 0;

    const variants = {
        success: 'bg-success-10 text-success border-success-20',
        warning: 'bg-warning-10 text-warning border-warning-20',
        error: 'bg-error-10 text-error border-error-20',
    };

    return (
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${variants[variant]}`}>
            <span className="text-sm font-medium">{label}</span>
            <span className="text-sm font-bold">{value}</span>
            <span className="text-xs opacity-70">({percentage.toFixed(0)}%)</span>
        </div>
    );
};
